import type { TupleIndices } from '@/types'

type CodonationCalculatorConfig = {
  upto: number
  suggest: number
  prefills: readonly [number, number, number]
  max: number
  minBucketValue: readonly [number, number, number, number, number, number]
  step: 1 | 10 | 100
}
type MinBucketIndex = TupleIndices<CodonationCalculatorConfig['minBucketValue']>

class CodonationCalculator {
  static defaults: ReadonlyArray<CodonationCalculatorConfig> = [
    {
      upto: 200,
      suggest: 50,
      prefills: [150, 50, 25],
      max: 120,
      minBucketValue: [0, 1, 30, 60, 80, 100],
      step: 10,
    },
    {
      upto: 500,
      suggest: 100,
      prefills: [200, 50, 25],
      max: 250,
      minBucketValue: [0, 1, 50, 110, 150, 200],
      step: 10,
    },
    {
      upto: 1000,
      suggest: 200,
      prefills: [300, 100, 50],
      max: 500,
      minBucketValue: [0, 1, 100, 250, 350, 450],
      step: 100,
    },
    {
      upto: 1500,
      suggest: 300,
      prefills: [400, 200, 100],
      max: 800,
      minBucketValue: [0, 1, 200, 400, 600, 800],
      step: 100,
    },
    {
      upto: 1900,
      suggest: 400,
      prefills: [500, 200, 100],
      max: 1000,
      minBucketValue: [0, 1, 300, 500, 700, 900],
      step: 100,
    },
    {
      upto: 2500,
      suggest: 500,
      prefills: [500, 300, 200],
      max: 1300,
      minBucketValue: [0, 1, 400, 600, 800, 1200],
      step: 100,
    },
    {
      upto: 3000,
      suggest: 500,
      prefills: [700, 400, 200],
      max: 1300,
      minBucketValue: [0, 1, 400, 600, 800, 1200],
      step: 100,
    },
    {
      upto: 4000,
      suggest: 600,
      prefills: [700, 500, 300],
      max: 1500,
      minBucketValue: [0, 1, 500, 700, 900, 1300],
      step: 100,
    },
    {
      upto: 5000,
      suggest: 900,
      prefills: [900, 500, 400],
      max: 2300,
      minBucketValue: [0, 1, 600, 1000, 1300, 2000],
      step: 100,
    },
    {
      upto: 8000,
      suggest: 900,
      prefills: [1200, 700, 500],
      max: 2300,
      minBucketValue: [0, 1, 600, 1000, 1300, 2000],
      step: 100,
    },
    {
      upto: 10000,
      suggest: 1200,
      prefills: [1500, 900, 500],
      max: 3000,
      minBucketValue: [0, 1, 800, 1300, 1800, 2700],
      step: 100,
    },
    {
      upto: 15000,
      suggest: 2000,
      prefills: [3000, 1000, 500],
      max: 5000,
      minBucketValue: [0, 1, 1200, 2100, 3000, 4400],
      step: 100,
    },
    {
      upto: 20000,
      suggest: 2000,
      prefills: [3000, 1000, 500],
      max: 5000,
      minBucketValue: [0, 1, 1200, 2100, 3000, 4400],
      step: 100,
    },
    {
      upto: 30000,
      suggest: 4000,
      prefills: [5000, 2000, 1000],
      max: 10000,
      minBucketValue: [0, 1, 2500, 4200, 5900, 8900],
      step: 100,
    },
    {
      upto: 40000,
      suggest: 4000,
      prefills: [5000, 2000, 1000],
      max: 10000,
      minBucketValue: [0, 1, 2500, 4200, 5900, 8900],
      step: 100,
    },
    {
      upto: 50000,
      suggest: 5000,
      prefills: [7000, 2000, 1000],
      max: 13000,
      minBucketValue: [0, 1, 3100, 5200, 7400, 11100],
      step: 100,
    },
    {
      upto: 100000,
      suggest: 6000,
      prefills: [8000, 5000, 2000],
      max: 15000,
      minBucketValue: [0, 1, 3800, 6200, 8800, 13400],
      step: 100,
    },
    {
      upto: Infinity,
      suggest: 7000,
      prefills: [10000, 5000, 2000],
      max: 18000,
      minBucketValue: [0, 1, 4400, 7200, 10300, 15600],
      step: 100,
    },
  ] as const

  static findConfig(amount: number) {
    return this.defaults.find((e) => e.upto >= amount)!
  }

  static suggest(amount: number) {
    return this.findConfig(amount).suggest
  }

  static prefills(amount: number) {
    return this.findConfig(amount).prefills
  }

  static step(amount: number) {
    return this.findConfig(amount).step
  }

  static getCodonationHelpers(amount: number) {
    const config = this.findConfig(amount)
    const max = this.max(amount)
    const step = this.step(amount)

    function findBucketIndexByValue(codonationValue: number) {
      const index = config.minBucketValue.findLastIndex((v) => v <= codonationValue) as MinBucketIndex
      if (index < 0) return null
      return index
    }
    function findBucketIndex(percent: number) {
      const value = CodonationCalculator.round((percent / 100) * max, step)
      return findBucketIndexByValue(value)
    }

    return {
      findBucketIndex,
      findBucketIndexByValue,
    }
  }

  static round(value: number, step: number) {
    return Math.round(Math.round(value / step) * step)
  }

  static max(amount: number) {
    // pick the smaller value between the amount and the max value of the config
    // round to the nearest step
    const config = this.findConfig(amount)
    const step = config.step
    const result = this.round(Math.min(config.max, amount), step)
    return result
  }
}

export default CodonationCalculator
